import { computed, observable } from 'mobx';
import { CXBaseBlock } from '~/components/ClientExperience/OneFlow/models/cx-base-block.model';
import type { CXInvoiceBlock } from '~/components/ClientExperience/OneFlow/models/cx-invoice-block.model';
import type { CXPaymentBlock } from '~/components/ClientExperience/OneFlow/models/cx-payment-block.model';
import { Variable } from '~/models/variable.model';
import {
    IBillFromContent,
    IInvoiceHeaderBlockResponse,
    IInvoiceHeaderVariableValue,
    IInvoiceProjectDetails
} from '~/types/server.types';
import { IImage, IInvoiceHeader } from '~/types/model.types';
import type { CXRecurringScheduleBlock } from '~/components/ClientExperience/OneFlow/models/cx-recurring-schedule-block.model';
import type { CXRootStore } from '~/components/ClientExperience/stores/cx-root.store';
import type { FlowRevision } from '~/models/flow-revision.model';
import { IInvoiceHeaderBlockStyles } from '~/types/block-styles.types';
import {
    formattedBillFrom,
    formattedProjectDetails
} from '~/components/Builder/Block/InvoiceBlock/InvoiceHeaderBlock.utils';

export class CXInvoiceHeaderBlock extends CXBaseBlock {
    type: 'invoice_header_block';
    styles: IInvoiceHeaderBlockStyles;
    @observable invoice_header: IInvoiceHeader;

    constructor(
        block: IInvoiceHeaderBlockResponse,
        flowRevision: FlowRevision<CXRootStore>,
        rootStore: CXRootStore
    ) {
        super(block, flowRevision, rootStore);
        this.type = block.type;
        this.styles = block.styles;
        this.invoice_header = block.invoice_header;
    }

    @computed
    get paymentScheduleBlock() {
        return (this.parent as CXInvoiceBlock).invoicePaymentScheduleBlock;
    }

    @computed
    get nextPaymentBlock(): CXPaymentBlock {
        return this.paymentScheduleBlock?.nextPaymentBlock;
    }

    @computed
    get currentPaymentBlock() {
        return this.isRecurringInvoice
            ? this.recurringScheduleBlock?.currentPaymentBlock
            : this.nextPaymentBlock;
    }

    @computed
    get dateIssued() {
        return this.flow.first_published_at || new Date();
    }

    @computed
    get recurringScheduleBlock(): CXRecurringScheduleBlock {
        return (this.parent as CXInvoiceBlock).invoiceRecurringScheduleBlock;
    }

    @computed
    get isRecurringInvoice(): boolean {
        return (this.parent as CXInvoiceBlock).isRecurringInvoice;
    }

    get connectedVariable(): Variable<IInvoiceHeaderVariableValue> {
        return super.connectedVariable;
    }

    @computed
    get invoiceNumber(): string {
        return this.connectedVariable?.value?.invoice_number;
    }

    @computed
    get poNumber(): string {
        return this.connectedVariable?.value?.po_number;
    }

    @computed
    get billToContent(): string {
        return this.connectedVariable?.value?.bill_to_content;
    }

    @computed
    get title(): string {
        return this.connectedVariable?.value?.title;
    }

    @computed
    get billFromContent(): IBillFromContent {
        return this.invoice_header?.bill_from_content;
    }

    @computed
    get companyLogoUrl(): string {
        return this.billFromContent?.company_icon_url;
    }

    @computed
    get companyLogo(): IImage {
        return (
            this.billFromContent?.company_icon ??
            this.rootStore?.flowStore?.minimalFlow?.branding_data?.icon ??
            this.rootStore?.flowViewUiStore?.selectedFlow?.company?.icon_image
        );
    }
    @computed
    get isDefaultHBLogo(): boolean {
        return this.billFromContent?.is_default_company_icon;
    }

    @computed
    get formattedBillFrom(): string {
        const details = this.invoice_header?.bill_from_content;
        if (!details) {
            return null;
        }

        return formattedBillFrom(details);
    }

    @computed
    get projectDetails(): IInvoiceProjectDetails {
        return this.invoice_header?.project_details;
    }

    get formattedProjectDetails(): string {
        const details = this.projectDetails;
        if (!details) {
            return null;
        }

        return formattedProjectDetails(details);
    }

    @computed
    get invoiceId(): string {
        const { selectedFlow } = this.rootStore.flowViewUiStore;
        return selectedFlow.paymentsBlocks[0].invoiceId;
    }

    @computed
    get shouldDisplayBillTo(): boolean {
        return (
            !this.flow.isLeadForm &&
            this.styles.show_bill_to &&
            !!this.billToContent
        );
    }
}
